/* eslint-disable react-refresh/only-export-components -- form helpers live next to the form */
import { Suspense, useState } from 'react'
import { lazyChunk } from '../../lib/lazyChunk.js'
import { useTranslation } from 'react-i18next'
import { Field, inputClass, PhotoUpload } from './ui.jsx'

const LocationPicker = lazyChunk(() => import('./LocationPicker.jsx'))
const LANGS = [
  ['en', 'En'],
  ['ru', 'Ru'],
  ['el', 'El'],
]

export const emptyFeeder = {
  slug: '',
  device_id: '',
  name: { en: '', ru: '', el: '' },
  description: { en: '', ru: '', el: '' },
  city: '',
  address: '',
  lat: null,
  lng: null,
  camera_url: '',
  photo_url: null,
  is_public: true,
  status: 'active',
}

const clean = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v))

/** Turn form state into an API body (create or patch). */
export function toBody(f, { create }) {
  const body = {
    name: clean(f.name),
    description: clean(f.description),
    city: f.city || null,
    address: f.address || null,
    lat: Number(f.lat),
    lng: Number(f.lng),
    camera_url: f.camera_url?.trim() || null,
    photo_url: f.photo_url || null,
    is_public: f.is_public,
  }
  if (create) return { ...body, slug: f.slug.trim(), device_id: f.device_id.trim() }
  return { ...body, status: f.status }
}

export default function FeederForm({ value, onChange, create = false }) {
  const { t } = useTranslation()
  const set = (patch) => onChange({ ...value, ...patch })
  const setI18n = (key, lang, text) => set({ [key]: { ...value[key], [lang]: text } })
  const [slugTouched, setSlugTouched] = useState(false)

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {create && (
        <>
          <Field label={t('admin.form.slug')} hint={t('admin.form.slugHint')}>
            <input
              className={inputClass}
              required
              pattern="[a-z0-9\-]{3,64}"
              value={value.slug}
              onChange={(e) => {
                setSlugTouched(true)
                set({ slug: e.target.value.toLowerCase() })
              }}
            />
          </Field>
          <Field label={t('admin.form.deviceId')} hint={t('admin.form.deviceIdHint')}>
            <input
              className={inputClass}
              required
              pattern="[A-Za-z0-9_\-]{3,64}"
              value={value.device_id}
              onChange={(e) => set({ device_id: e.target.value })}
            />
          </Field>
        </>
      )}
      {LANGS.map(([lang]) => (
        <Field key={`n-${lang}`} label={t(`admin.form.name${lang[0].toUpperCase()}${lang[1]}`)}>
          <input
            className={inputClass}
            required={lang === 'en'}
            value={value.name[lang] ?? ''}
            onChange={(e) => {
              setI18n('name', lang, e.target.value)
              if (create && lang === 'en' && !slugTouched) {
                set({
                  name: { ...value.name, en: e.target.value },
                  slug: e.target.value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
                })
              }
            }}
          />
        </Field>
      ))}
      <div className="hidden md:block" />
      {LANGS.map(([lang]) => (
        <Field key={`d-${lang}`} label={t(`admin.form.desc${lang[0].toUpperCase()}${lang[1]}`)} className="md:col-span-2">
          <textarea
            className={`${inputClass} h-20 py-2`}
            value={value.description[lang] ?? ''}
            onChange={(e) => setI18n('description', lang, e.target.value)}
          />
        </Field>
      ))}
      <Field label={t('admin.form.city')}>
        <input className={inputClass} value={value.city ?? ''} onChange={(e) => set({ city: e.target.value })} />
      </Field>
      <Field label={t('admin.form.address')}>
        <input className={inputClass} value={value.address ?? ''} onChange={(e) => set({ address: e.target.value })} />
      </Field>
      <div className="flex flex-col gap-2 md:col-span-2">
        <span className="text-sm font-extrabold">{t('admin.form.pickOnMap')}</span>
        <Suspense fallback={<div className="h-72 rounded-2xl bg-mint" />}>
          <LocationPicker lat={value.lat} lng={value.lng} onPick={(lat, lng) => set({ lat, lng })} />
        </Suspense>
      </div>
      <Field label={t('admin.form.lat')}>
        <input
          className={inputClass}
          type="number"
          step="any"
          required
          min="-90"
          max="90"
          value={value.lat ?? ''}
          onChange={(e) => set({ lat: e.target.value === '' ? null : Number(e.target.value) })}
        />
      </Field>
      <Field label={t('admin.form.lng')}>
        <input
          className={inputClass}
          type="number"
          step="any"
          required
          min="-180"
          max="180"
          value={value.lng ?? ''}
          onChange={(e) => set({ lng: e.target.value === '' ? null : Number(e.target.value) })}
        />
      </Field>
      <Field label={t('admin.form.cameraUrl')} hint={t('admin.form.cameraUrlHint')} className="md:col-span-2">
        <input
          className={inputClass}
          placeholder="rtsp://user:pass@10.8.0.2:554/stream1"
          value={value.camera_url ?? ''}
          onChange={(e) => set({ camera_url: e.target.value })}
        />
      </Field>
      {!create && (
        <>
          <Field label={t('admin.form.status')}>
            <select className={inputClass} value={value.status} onChange={(e) => set({ status: e.target.value })}>
              {['active', 'maintenance', 'disabled'].map((s) => (
                <option key={s} value={s}>
                  {t(`admin.status.${s}`)}
                </option>
              ))}
            </select>
          </Field>
          <div className="flex flex-col gap-1.5 text-sm font-extrabold">
            {t('admin.form.photo')}
            <PhotoUpload value={value.photo_url} onChange={(url) => set({ photo_url: url })} />
          </div>
        </>
      )}
      <label className="flex items-center gap-2 text-sm font-extrabold">
        <input
          type="checkbox"
          className="size-5 accent-teal"
          checked={value.is_public}
          onChange={(e) => set({ is_public: e.target.checked })}
        />
        {t('admin.form.public')}
      </label>
    </div>
  )
}
